import type {PropertyValue} from '@github-ui/custom-properties-types'
import {isEmptyPropertyValue, isPropertyValueArray} from '@github-ui/custom-properties-types/helpers'
import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Button, SelectPanel, type SelectPanelItemInput as ItemInput, Truncate} from '@primer/react'
import {type RefCallback, useId, useMemo, useRef, useState} from 'react'

import styles from './CustomPropertySelectPanel.module.css'

export const mixedValuePlaceholder = '(Mixed)'

const EMPTY_STATE_MESSAGE: {title: string; body: string; variant: 'empty'} = {
  title: 'No property values were found',
  body: 'Try searching for a different value for results.',
  variant: 'empty',
}

const MAX_DISPLAYED_ALLOWED_VALUES = 200
export interface CustomPropertySelectPanelProps {
  propertyName: string
  defaultValue: PropertyValue | null
  allowedValues: string[] | null
  propertyValue?: PropertyValue
  setRef?: RefCallback<HTMLElement>
  mixed: boolean
  onChange: (value: PropertyValue) => void
  anchorProps?: Omit<React.HTMLAttributes<HTMLElement>, 'onFocus' | 'onMouseOver'>
  fetchAllowedValuesUrl?: (propertyName: string) => string
}

export interface CustomPropertySingleSelectPanelProps extends CustomPropertySelectPanelProps {
  propertyValue: string
}
export function CustomPropertySingleSelectPanel({
  setRef,
  propertyName,
  fetchAllowedValuesUrl,
  defaultValue,
  allowedValues,
  propertyValue,
  mixed,
  onChange,
  anchorProps,
}: CustomPropertySingleSelectPanelProps) {
  const selectPanelProps = useSelectPanelProps({
    propertyName,
    defaultValue,
    allowedValues,
    mixed,
    initialSelection: propertyValue ? [propertyValue] : [],
    fetchAllowedValuesUrl,
    anchorProps,
    setRef,
  })
  const selectedItem = selectPanelProps.items.find(item => item.text && propertyValue === item.text)

  return (
    <SelectPanel
      {...selectPanelProps}
      onSelectedChange={(newItemValue?: ItemInput) => onChange(newItemValue?.text || '')}
      selected={selectedItem}
      message={selectPanelProps.items.length === 0 ? EMPTY_STATE_MESSAGE : undefined}
    />
  )
}

interface CustomPropertyMultiSelectPanelProps extends CustomPropertySelectPanelProps {
  propertyValue: string[]
}
export function CustomPropertyMultiSelectPanel({
  setRef,
  propertyName,
  defaultValue,
  allowedValues,
  propertyValue,
  mixed,
  onChange,
  anchorProps,
  fetchAllowedValuesUrl,
}: CustomPropertyMultiSelectPanelProps) {
  const selectPanelProps = useSelectPanelProps({
    propertyName,
    defaultValue,
    allowedValues,
    mixed,
    fetchAllowedValuesUrl,
    initialSelection: propertyValue,
    anchorProps,
    setRef,
  })
  const selectedItems = selectPanelProps.items.filter(item => item.selected)

  return (
    <SelectPanel
      {...selectPanelProps}
      onSelectedChange={(newItemValues: ItemInput[]) => {
        const {filterValue} = selectPanelProps
        const newValues = newItemValues.map(item => item.text || '')
        if (!filterValue) {
          onChange(newValues)
        } else {
          const isFilteredOut = (value: string) => !value.toLowerCase().includes(filterValue.toLowerCase())
          const hiddenSelectedValues = propertyValue.filter(isFilteredOut)
          onChange([...hiddenSelectedValues, ...newValues])
        }
      }}
      selected={selectedItems}
      message={selectPanelProps.items.length === 0 ? EMPTY_STATE_MESSAGE : undefined}
    />
  )
}

interface UseSelectPanelProps {
  propertyName: string
  defaultValue: PropertyValue | null
  allowedValues: string[] | null
  mixed: boolean
  initialSelection: string[]
  anchorProps?: React.HTMLAttributes<HTMLElement>
  fetchAllowedValuesUrl?: (propertyName: string) => string
  setRef?: RefCallback<HTMLElement>
}

const maxItemsViewport = 7
function useSelectPanelProps(props: UseSelectPanelProps) {
  const {defaultValue, mixed, initialSelection, anchorProps, setRef} = props
  const {data: allowedValues, isLoading, requestFetch} = useFetchAllowedValues(props)

  const localRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setOpen] = useState(false)
  const [filterText, setFilterText] = useState('')

  const allItems = useMemo(
    () => (allowedValues || []).map(value => toItemInput(value, initialSelection.includes(value))),
    [allowedValues, initialSelection],
  )
  const items = getDisplayItems(allItems, filterText.toLowerCase()).filter(limitAllowedValuesList())
  const height: 'medium' | 'auto' = items.length > maxItemsViewport ? 'medium' : 'auto'

  const anchorLabel = getDisplayAnchorLabel(initialSelection, defaultValue, mixed)
  const anchorLabelId = useId()

  return {
    items,
    anchorRef: localRef,
    renderAnchor: (panelAnchorProps: React.HTMLAttributes<HTMLElement>) => (
      <Button
        block
        alignContent="start"
        trailingAction={TriangleDownIcon}
        className={styles.CustomPropertySelectPanelButton}
        {...panelAnchorProps}
        {...anchorProps}
        onMouseOver={requestFetch}
        onFocus={requestFetch}
        ref={el => {
          localRef.current = el
          setRef?.(el)
        }}
        aria-describedby={[anchorLabelId, panelAnchorProps['aria-describedby'], anchorProps?.['aria-describedby']]
          .filter(Boolean)
          .join(' ')}
      >
        <Truncate maxWidth="100%" title={anchorLabel} id={anchorLabelId}>
          {anchorLabel}
        </Truncate>
      </Button>
    ),
    placeholderText: 'Search for values',
    filterValue: filterText,
    open: isOpen,
    onFilterChange: setFilterText,
    onOpenChange: (newIsOpen: boolean) => {
      setOpen(newIsOpen)
      setFilterText('')
    },
    loading: isLoading,
    overlayProps: {height, width: 'medium' as const},
  }
}

function toItemInput(value: string, selected: boolean): ItemInput {
  return {
    id: value,
    text: value,
    selected,
  }
}

function getDisplayItems(allItems: ItemInput[], filter: string) {
  const matchesFilter = (item: ItemInput) => (item.text || '').toLowerCase().includes(filter)
  const items = filter ? allItems.filter(matchesFilter) : allItems
  if (items.length === 0) {
    return []
  }
  return items
}

export function getDisplayAnchorLabel(
  value: PropertyValue,
  defaultValue: PropertyValue | null,
  mixed: boolean,
): string {
  if (mixed) {
    return mixedValuePlaceholder
  }

  if (!isEmptyPropertyValue(value)) {
    return getValueLabel(value)
  } else if (!isEmptyPropertyValue(defaultValue)) {
    return `Default (${getValueLabel(defaultValue || '')})`
  } else {
    return 'Choose an option'
  }
}

function getValueLabel(value: PropertyValue): string {
  if (isPropertyValueArray(value)) {
    if (value.length <= 1) {
      return value[0] || ''
    } else {
      return `${value.length} selected`
    }
  } else {
    return value
  }
}

// Limits number of allowed values to MAX_DISPLAYED_ALLOWED_VALUES while always keeping selected items in the list
function limitAllowedValuesList() {
  let limit = MAX_DISPLAYED_ALLOWED_VALUES
  return (item: ItemInput) => {
    if (item.selected) {
      limit--
      return true
    }

    if (limit <= 0) return false
    limit--
    return true
  }
}

interface AllowedValuesResponsePayload {
  allowedValues: string[]
}

interface UseFetchAllowedValuesProps {
  propertyName: string
  allowedValues: string[] | null
  fetchAllowedValuesUrl?: (propertyName: string) => string
}

function useFetchAllowedValues({propertyName, allowedValues, fetchAllowedValuesUrl}: UseFetchAllowedValuesProps) {
  const [fetchRequested, setFetchRequested] = useState(false)
  const fetchUrl = fetchAllowedValuesUrl?.(propertyName)

  const {data, isLoading} = useQuery({
    queryKey: ['queryCustomPropertyAllowedValues', propertyName, fetchUrl],
    queryFn: async () => {
      const result = await verifiedFetchJSON(fetchUrl || '')
      if (result.ok) {
        return (await result.json()) as AllowedValuesResponsePayload
      }

      throw new Error(`${propertyName}: Failed to fetch allowed values.`)
    },
    select: response => response?.allowedValues,
    enabled: !allowedValues && !!fetchUrl && fetchRequested,
    initialData: allowedValues ? {allowedValues} : undefined,
  })

  if (!fetchUrl && !allowedValues) {
    throw new Error(`Allowed values cannot be fetched for ${propertyName}. fetchAllowedValuesUrl is not configured.`)
  }

  return {
    data: fetchAllowedValuesUrl ? data : allowedValues, // Directly use allowedValues when fetch URL not applicable, such as the edit definition form
    isLoading,
    requestFetch: () => setFetchRequested(true),
  }
}
